import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { ArrowUpDown, Flag, MoreHorizontal, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  DataTable,
  HelpPopover,
  NotPermitted,
  ReasonDialog,
  StatusChip,
  type IDataColumn
} from '@/design';
import { ApiError, ProblemType, type IEtagged } from '@/domains/api';
import type {
  IRankingEdit,
  IRankingTerritory,
  IRankingTerritoryPlacement
} from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { rankingsApi } from '../../api/rankings-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { type ParticipantPick, ParticipantPicker } from '../../components/participant-picker';
import { personHubPath } from '../../components/participant-link';
import { RankingFlags } from '../../components/ranking-flags';
import { wordFor } from '../../figures';
import { rankingPath } from '../../paths';
import { RANKING_REFUSAL_WORDS, ROW_ORIGIN_WORDS, rankingRefusalReason } from '../../rankings';
import { refusalMessage } from '../../refusal';
import { sentence, unanswered } from '../person-hub/said';
import { useCan } from '@/domains/auth';
import { FlaggedPlacements, useFlaggedPlacements } from './flagged-placements';
import { useRanking } from './ranking';
import { useTerritoryNames } from './territories';

/**
 * A ranking's Placements tab (SR-231): its territories down the left with their counts and `gap` /
 * `duplicate` chips, the chosen one's placements beside them — a national list skips the column
 * and draws its one table. The territory chosen is the URL's (`?territory=`), the first by default.
 *
 * **Edits are a draft's, and `rankings.manage`'s** — Insert over the table, Move and Remove on a
 * row; each needs a reason (SR-507), sends the `ETag` the record holds and hands the record the one
 * it answers. For anyone without the permission they are drawn disabled, with the reason once over
 * the table; a published ranking draws none. **A row the list's criteria flag** (SR-506: gender,
 * age, territory) says so under the name; an edit that lands on one adds it to the status line.
 * **Flagged** heads the territory list — every flagged row across territories, in the table's
 * place, each territory's count beside it — and on a national list filters its table in place.
 *
 * **The answer is patched in, not re-read**: the participant's row (an insert's or a move's, drawn
 * here when its `territory` is the shown one), every row of the shown
 * territory the edit `shifted`, each flashing `old → new` for two seconds, and a status line saying
 * what moved. The record reads its overview again for the counts.
 *
 * **Refusals are `role="alert"`**: a stale `ETag` (409 `revision-conflict`) above the table with
 * Reload — nothing was saved; an insert's 404 (the ranking gone from its address) there too, with
 * Read again; `already-placed`, a move's or removal's 404 and the body's 400s inside the form or
 * dialog that made them, which stays open; `ranking-published` above the table, the record read
 * again. The header's "Read again" reads the rows too, and clears what the last write was told.
 *
 * **Edits wait for the rows**: an answer is laid over the rows read, so none is offered before
 * they are. **Insert finds its participant through People**, which is `responses.browse`'s: without
 * it, Insert is drawn disabled with the reason, and a name is text rather than a link to the hub.
 * A territorial draft with no territory left still offers Insert, the territory typed. The
 * participant chosen fills the territory box with their territory in force for the list, until
 * staff type in it (SR-319).
 */

const FLASH_MS = 2000;

/** The `operationId` of an edit that wrote nothing (survey-api's no-op move). */
const NO_OPERATION = '00000000-0000-0000-0000-000000000000';

interface IFlash {
  old: Nullable<number>;
  now: number;
}

interface IShownRows {
  territory: Nullable<string>;
  rows: IRankingTerritoryPlacement[];
}

function byPlace(left: IRankingTerritoryPlacement, right: IRankingTerritoryPlacement): number {
  return left.place - right.place || left.displayName.localeCompare(right.displayName);
}

/** Where survey-api put the row — it clamps a place past the end — or the place asked. */
function placedAt(edit: IRankingEdit, asked: number): number {
  return edit.placement?.place ?? asked;
}

function rowsWord(n: number): string {
  return n === 1 ? '1 row' : `${n} rows`;
}

/** What the last edit said — its status line and the rows it moved — held by the tab, above the
 *  table: an insert into a territorial draft with no territory makes the first one, which remounts
 *  the table under that territory's key, and the confirmation has to outlive it. */
interface IConfirmation {
  flash: ReadonlyMap<string, IFlash>;
  setFlash: (flash: ReadonlyMap<string, IFlash>) => void;
  status: Nullable<string>;
  setStatus: (status: Nullable<string>) => void;
}

/** The id of the page's `<datalist>` of the list's live territory names — what a territory box
 *  suggests, free text still allowed. */
const TerritorySuggestions = React.createContext<string | undefined>(undefined);

export function RankingPlacementsTab() {
  const record = useRanking();
  const suggestionsId = React.useId();
  const names = useTerritoryNames(
    record.key.surveyCode,
    record.key.list,
    record.overview?.ranking.territorial === true
  );
  const [params] = useSearchParams();
  const overview = record.overview;
  const [flash, setFlash] = React.useState<ReadonlyMap<string, IFlash>>(new Map());
  const [status, setStatus] = React.useState<Nullable<string>>(null);
  const confirmation = React.useMemo<IConfirmation>(
    () => ({ flash, setFlash, status, setStatus }),
    [flash, status]
  );
  // "Flagged" chosen in a territorial list's territories (SR-506): its rows across territories.
  const flagged = params.get('flagged') === '1';
  const territorial = overview?.ranking.territorial === true;
  const flaggedRead = useFlaggedPlacements(territorial);
  const territory =
    territorial && !flagged
      ? (params.get('territory') ?? overview.territories[0]?.territory ?? null)
      : null;
  // Another territory chosen drops the last edit's words — but not a territory an edit emptied
  // (to none) or made out of none, which are that edit's own.
  const shown = React.useRef(territory);
  React.useEffect(() => {
    if (shown.current !== null && territory !== null && shown.current !== territory) {
      setFlash(new Map());
      setStatus(null);
    }
    shown.current = territory;
  }, [territory]);
  React.useEffect(() => {
    if (flash.size === 0) return undefined;
    const timer = setTimeout(() => setFlash(new Map()), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flash]);

  if (overview === null) {
    return (
      <Alert>
        <AlertTitle>Not exported yet</AlertTitle>
        <AlertDescription>
          No ranking is kept at this address yet — its{' '}
          <Link className="underline" to={rankingPath(record.key, 'export')}>
            Export for scoring
          </Link>{' '}
          tab makes the draft when it writes the file.
        </AlertDescription>
      </Alert>
    );
  }
  const { territories, ranking } = overview;

  const table = (
    <TerritoryTable
      key={territory ?? ''}
      territory={territory}
      territorial={ranking.territorial}
      initiallyFlagged={flagged}
      confirmation={confirmation}
    />
  );
  if (!ranking.territorial) return table;
  return (
    <TerritorySuggestions.Provider value={suggestionsId}>
      <datalist id={suggestionsId} data-territory-suggestions>
        {names.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      <div className="grid items-start gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
        <TerritoryList
          territories={territories}
          chosen={territory}
          flagged={flaggedRead.data}
          flaggedChosen={flagged}
        />
        {flagged ? (
          <FlaggedPlacements read={flaggedRead} />
        ) : territory === null && ranking.status !== 'draft' ? (
          <p className="text-sm text-muted-foreground">This ranking places nobody yet.</p>
        ) : (
          table
        )}
      </div>
    </TerritorySuggestions.Provider>
  );
}

/** The territories, "Flagged" above them (SR-506) — every flagged row of the ranking — and each
 *  territory's own flagged count beside its placements. */
function TerritoryList({
  territories,
  chosen,
  flagged,
  flaggedChosen
}: {
  territories: IRankingTerritory[];
  chosen: Nullable<string>;
  flagged: Nullable<IRankingTerritoryPlacement[]>;
  flaggedChosen: boolean;
}) {
  const record = useRanking();
  const flaggedIn = (name: string) => flagged?.filter((row) => row.territory === name).length ?? 0;
  return (
    <nav aria-label="Territories" className="rounded-md border border-border bg-white">
      <ul className="max-h-[calc(100dvh-16rem)] overflow-y-auto py-1">
        <li className="mb-1 border-b border-border pb-1">
          <Link
            to={rankingPath(record.key, 'placements', '?flagged=1')}
            aria-current={flaggedChosen ? 'page' : undefined}
            data-flagged-filter
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted',
              flaggedChosen && 'bg-[var(--color-slight-blue)] font-medium'
            )}
          >
            <Flag aria-hidden className="size-3.5 text-[var(--color-deep-magenta)]" />
            <span className="min-w-0 flex-1 truncate">Flagged</span>
            <span
              className={cn(
                'text-xs tabular-nums',
                flagged && flagged.length > 0
                  ? 'font-medium text-[var(--color-deep-magenta)]'
                  : 'text-muted-foreground'
              )}
            >
              {flagged === null ? '…' : flagged.length}
            </span>
          </Link>
        </li>
        {territories.map((entry) => {
          const name = entry.territory ?? '';
          const current = !flaggedChosen && name === chosen;
          const flags = flaggedIn(name);
          return (
            <li key={name}>
              <Link
                to={rankingPath(record.key, 'placements', `?territory=${encodeURIComponent(name)}`)}
                aria-current={current ? 'page' : undefined}
                data-territory={name}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted',
                  current && 'bg-[var(--color-slight-blue)] font-medium'
                )}
              >
                <span className="min-w-0 flex-1 truncate">{name}</span>
                {entry.gaps && <StatusChip tone="attention">gap</StatusChip>}
                {entry.duplicates && <StatusChip tone="danger">duplicate</StatusChip>}
                {flags > 0 && (
                  <span
                    data-flagged-count={flags}
                    title={`${flags} flagged`}
                    className="inline-flex items-center gap-0.5 text-xs tabular-nums text-[var(--color-deep-magenta)]"
                  >
                    <Flag aria-hidden className="size-3" />
                    {flags}
                    <span className="sr-only"> flagged</span>
                  </span>
                )}
                <span className="text-xs text-muted-foreground tabular-nums">
                  {entry.placements}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

type Refusal = { kind: 'conflict' } | { kind: 'gone' } | { kind: 'said'; text: string };

function TerritoryTable({
  territory,
  territorial,
  initiallyFlagged,
  confirmation
}: {
  territory: Nullable<string>;
  territorial: boolean;
  /** The page opened on `?flagged=1`: a national list starts filtered to its flagged rows. */
  initiallyFlagged: boolean;
  confirmation: IConfirmation;
}) {
  const record = useRanking();
  const { key, editable, permitted, freshness, cleared } = record;
  const { flash, setFlash, status, setStatus } = confirmation;
  const browses = useCan()('responses.browse');
  const draft = record.overview?.ranking.status === 'draft';
  const reasonId = React.useId();
  const searchId = React.useId();
  // A territorial list's draft with no territory left reads nothing: there is no territory to ask.
  const none = territorial && territory === null;
  const { data, error, adopt } = useAdminData<IShownRows>(
    async (signal) => {
      if (none) return { territory, rows: [] };
      try {
        return { territory, rows: await rankingProcessApi.placements(key, territory, signal) };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not read the placements'));
      }
    },
    [freshness]
  );
  const [refusal, setRefusal] = React.useState<Nullable<Refusal>>(null);
  const [inserting, setInserting] = React.useState(false);
  const [moving, setMoving] = React.useState<Nullable<IRankingTerritoryPlacement>>(null);
  const [removing, setRemoving] = React.useState<Nullable<IRankingTerritoryPlacement>>(null);
  // The row whose "⋯" menu opened a dialog: the dialog gone, the focus goes back to that menu's
  // button, since the menu item it was opened from has gone with the menu.
  const menuOf = React.useRef<Nullable<string>>(null);
  const backToMenu = () => {
    const participantId = menuOf.current;
    menuOf.current = null;
    if (participantId === null) return;
    document
      .querySelector<HTMLElement>(`[data-more-for="${CSS.escape(participantId)}"]`)
      ?.focus();
  };

  // A "Read again" that landed draws the ranking as it now is: what the last write was told no
  // longer applies. One that failed leaves it said.
  React.useEffect(() => setRefusal(null), [cleared]);

  const rows = data?.rows ?? null;
  // A national list's quick filter (SR-506): its flagged rows alone, in this same table.
  const [onlyFlagged, setOnlyFlagged] = React.useState(!territorial && initiallyFlagged);
  const flaggedRows = rows?.filter((row) => (row.flags?.length ?? 0) > 0);
  const flaggedCount = flaggedRows?.length ?? 0;
  const shownRows = onlyFlagged ? flaggedRows : rows;
  const canInsert = editable && browses && rows !== null;
  const where = territorial && territory ? ` in ${territory}` : '';

  /** The answer laid over the rows: see the module's comment. */
  const landed = (
    answer: IEtagged<IRankingEdit>,
    participantId: string,
    describe: (edit: IRankingEdit) => string
  ) => {
    const edit = answer.body;
    record.adopt(answer.etag, edit.version);
    setRefusal(null);
    // A move to where the participant stands writes nothing: the current version, no operation,
    // nothing shifted — so nothing flashes and nothing is read again.
    if (edit.operationId === NO_OPERATION) {
      setFlash(new Map());
      setStatus('Nothing changed — they already stand there.');
      return;
    }
    const current = data?.rows ?? [];
    const before = current.find((row) => row.participantId === participantId);
    const here = (shiftTerritory: Nullable<string>) => !territorial || shiftTerritory === territory;
    const shifts = new Map(
      edit.shifted
        .filter((shift) => here(shift.territory))
        .map((shift) => [shift.participantId, shift])
    );
    let next = current
      .filter((row) => row.participantId !== participantId)
      .map((row) => {
        const shift = shifts.get(row.participantId);
        return shift ? { ...row, place: shift.newPlace } : row;
      });
    const lit = new Map<string, IFlash>(
      [...shifts.values()].map((shift) => [
        shift.participantId,
        { old: shift.oldPlace, now: shift.newPlace }
      ])
    );
    if (edit.placement && here(edit.placement.territory)) {
      next = [...next, edit.placement];
      lit.set(participantId, { old: before?.place ?? null, now: edit.placement.place });
    }
    adopt({ territory, rows: next.sort(byPlace) });
    setFlash(lit);
    setStatus(describe(edit));
    void record.reread();
  };

  /** A refusal in words for the form or dialog that made it — or `null` where the page says it. */
  const refused = (failure: unknown, insert: boolean): Nullable<string> => {
    if (failure instanceof ApiError && failure.isProblem(ProblemType.revisionConflict)) {
      setRefusal({ kind: 'conflict' });
      return null;
    }
    const reason = rankingRefusalReason(failure);
    if (reason !== null) {
      if (reason === 'ranking-published') {
        setRefusal({ kind: 'said', text: wordFor(RANKING_REFUSAL_WORDS, reason) });
        void record.reread();
        return null;
      }
      return wordFor(RANKING_REFUSAL_WORDS, reason);
    }
    if (failure instanceof ApiError && failure.status === 404) {
      // An insert names nobody placed, so its 404 is the ranking's own: gone from its address.
      if (insert) {
        setRefusal({ kind: 'gone' });
        return null;
      }
      return 'They are no longer placed in this ranking — read the page again.';
    }
    if (unanswered(failure)) {
      return 'The answer did not arrive, so whether the change landed is not known — read the page again before trying it again.';
    }
    return sentence(refusalMessage(failure, 'The change was refused'));
  };

  /** Runs one edit: `null` once landed, else what to say. */
  const write = async (
    send: (etag: string) => Promise<IEtagged<IRankingEdit>>,
    participantId: string,
    describe: (edit: IRankingEdit) => string,
    insert = false
  ): Promise<Nullable<string>> => {
    if (record.etag === null)
      return 'This ranking came without its version — read the page again.';
    try {
      landed(await send(record.etag), participantId, describe);
      return null;
    } catch (failure) {
      return refused(failure, insert);
    }
  };

  const columns: IDataColumn<IRankingTerritoryPlacement>[] = [
    {
      id: 'place',
      header: '#',
      numeric: true,
      cell: (row) => {
        const lit = flash.get(row.participantId);
        return (
          <span className="inline-flex items-center gap-1.5">
            {lit && lit.old !== null && lit.old !== lit.now && (
              <span data-shift className="text-xs font-normal text-[var(--color-teal)]">
                {lit.old} →
              </span>
            )}
            <span className="font-medium">{row.place}</span>
          </span>
        );
      }
    },
    {
      id: 'name',
      header: 'Participant',
      cell: (row) => (
        <>
          {browses ? (
            <Link to={personHubPath(row.participantId)} className="font-medium hover:underline">
              {row.displayName}
            </Link>
          ) : (
            <span className="font-medium">{row.displayName}</span>
          )}
          <RankingFlags flags={row.flags} />
        </>
      )
    },
    { id: 'firm', header: 'Firm', cell: (row) => row.firm ?? '' },
    {
      id: 'where',
      header: 'City, State',
      cell: (row) => [row.city, row.state].filter(Boolean).join(', ')
    },
    { id: 'origin', header: 'Origin', cell: (row) => wordFor(ROW_ORIGIN_WORDS, row.origin) },
    ...(draft
      ? [
          {
            id: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Actions',
            // A row's work as on the other work lists: the outlined next step, "⋯" for every press.
            cell: (row: IRankingTerritoryPlacement) => (
              <span className="flex items-center justify-end gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5"
                  disabled={!editable}
                  aria-label={`Move ${row.displayName}`}
                  aria-describedby={editable ? undefined : reasonId}
                  onClick={() => setMoving(row)}
                >
                  <ArrowUpDown className="size-4" aria-hidden />
                  Move
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      disabled={!editable}
                      aria-label={`More for ${row.displayName}`}
                      data-more-for={row.participantId}
                      aria-describedby={editable ? undefined : reasonId}
                    >
                      <MoreHorizontal className="size-4" aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      onSelect={() => {
                        menuOf.current = row.participantId;
                        setMoving(row);
                      }}
                    >
                      <ArrowUpDown className="size-4" aria-hidden />
                      Move…
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() => {
                        menuOf.current = row.participantId;
                        setRemoving(row);
                      }}
                    >
                      <Trash2 className="size-4" aria-hidden />
                      Remove…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </span>
            )
          }
        ]
      : [])
  ];

  const heading = territorial ? (territory ?? 'No territory yet') : 'Placements';
  return (
    <section aria-label={heading} className="grid gap-3" data-territory-table={territory ?? ''}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">
            {heading}
            {rows && <span className="ml-2 font-normal text-muted-foreground">· {rows.length}</span>}
          </h2>
          {!territorial && (flaggedCount > 0 || onlyFlagged) && (
            <button
              type="button"
              aria-pressed={onlyFlagged}
              data-flagged-filter
              onClick={() => setOnlyFlagged(!onlyFlagged)}
              className={cn(
                'ml-2 inline-flex h-6 items-center gap-1 rounded-full border px-2.5 text-xs text-[var(--color-deep-magenta)]',
                onlyFlagged
                  ? 'border-[var(--color-deep-magenta)] bg-[color-mix(in_srgb,var(--color-magenta)_8%,white)]'
                  : 'border-border bg-white hover:bg-muted'
              )}
            >
              <Flag aria-hidden className="size-3" />
              Flagged · {flaggedCount}
            </button>
          )}
          <HelpPopover label="About placements and flags">
            <p>
              Insert, Move and Remove change a draft and each needs a reason. The reason is kept
              with the change in History.
            </p>
            <p>
              A row is flagged when the participant’s own data on their survey does not fit the
              list: Gender is Male on a women’s list; the date of birth makes them over 40 on a
              Next-Gen list, on the as-of date of the ranking’s cycle (today when it has none); or,
              on a list ranked by territory, the territory their office gives — or staff’s
              override — is another one. A flag never stops a change or an import: an import sums
              its flags up in one line, and Flagged — above the territories, or beside a national
              list's heading — lists the flagged rows.
            </p>
          </HelpPopover>
        </div>
        {draft && (
          <Button
            type="button"
            size="sm"
            disabled={!canInsert || inserting}
            aria-describedby={!editable ? reasonId : browses ? undefined : searchId}
            onClick={() => setInserting(true)}
          >
            Insert
          </Button>
        )}
      </div>
      {draft && !permitted && <NotPermitted id={reasonId} what="changing a draft ranking" />}
      {draft && permitted && !browses && (
        <NotPermitted id={searchId} what="searching participants, which Insert finds one through" />
      )}
      {!draft && record.overview && (
        <p className="text-sm text-muted-foreground">Published — this ranking is read-only.</p>
      )}

      {refusal?.kind === 'conflict' && (
        <Alert variant="destructive">
          <AlertTitle>Someone changed this ranking</AlertTitle>
          <AlertDescription>
            <span>
              It changed since this page read it, so nothing was saved. Reload it and try again.
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => void record.readAgain()}
            >
              Reload
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {refusal?.kind === 'gone' && (
        <Alert variant="destructive">
          <AlertTitle>This ranking is no longer here</AlertTitle>
          <AlertDescription>
            <span>
              There is no ranking at this address any more, so nothing was inserted.
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => void record.readAgain()}
            >
              Refresh
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {refusal?.kind === 'said' && (
        <Alert variant="destructive">
          <AlertTitle>Not changed</AlertTitle>
          <AlertDescription>{refusal.text}</AlertDescription>
        </Alert>
      )}
      <p role="status" className={cn('text-sm', status ? 'text-[var(--color-teal)]' : 'sr-only')}>
        {status ?? ''}
      </p>

      {inserting && (
        <InsertForm
          territorial={territorial}
          territory={territory}
          onCancel={() => setInserting(false)}
          onInsert={async (pick, place, target, reason) => {
            const said = await write(
              (etag) =>
                rankingProcessApi.insert(key, etag, {
                  participantId: pick.participantId,
                  place,
                  territory: target,
                  reason
                }),
              pick.participantId,
              (edit) =>
                `Inserted ${pick.displayName} at ${placedAt(edit, place)}${
                  territorial ? ` in ${edit.placement?.territory ?? target}` : ''
                } — ${rowsWord(edit.shifted.length)} moved down.${flaggedNote(edit)}`,
              true
            );
            if (said === null) setInserting(false);
            return said;
          }}
        />
      )}

      {error && data !== null && (
        <LoadError message={`Could not refresh — showing the rows as last read. ${error}`} />
      )}
      <DataTable
        label={territorial && territory ? `Placements in ${territory}` : 'Placements'}
        columns={columns}
        rows={shownRows && shownRows.length > 0 ? shownRows : null}
        rowKey={(row) => row.participantId}
        rowProps={(row) =>
          ({
            'data-participant': row.participantId,
            'data-place': String(row.place),
            ...(flash.has(row.participantId) ? { 'data-shifted': '' } : {}),
            className: flash.has(row.participantId)
              ? 'bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] transition-colors'
              : 'transition-colors'
          }) as React.HTMLAttributes<HTMLTableRowElement>
        }
        maxHeight="none"
        state={
          rows === null
            ? error
              ? { kind: 'error', content: <LoadError message={error} /> }
              : { kind: 'loading' }
            : {
                kind: 'empty',
                content: onlyFlagged ? 'No placement is flagged.' : `Nobody is placed${where}.`
              }
        }
      />

      <MoveDialog
        row={moving}
        territorial={territorial}
        territory={territory}
        onClose={() => setMoving(null)}
        onClosed={backToMenu}
        onMove={(row, place, target, reason) =>
          write(
            (etag) =>
              rankingProcessApi.move(key, etag, row.participantId, {
                place,
                territory: target,
                reason
              }),
            row.participantId,
            (edit) => {
              const landedIn = edit.placement?.territory ?? target;
              return `Moved ${row.displayName} from ${row.place} to ${placedAt(edit, place)}${
                territorial && landedIn !== territory ? ` in ${landedIn}` : ''
              } — ${rowsWord(edit.shifted.length)} renumbered.${flaggedNote(edit)}`;
            }
          )
        }
      />
      <ReasonDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={removing ? `Remove ${removing.displayName}` : 'Remove'}
        description={
          removing
            ? `Takes ${removing.displayName} off place ${removing.place}${where}; everyone below moves up one.`
            : ''
        }
        required
        confirmLabel="Remove"
        onClosed={backToMenu}
        onConfirm={(reason) => {
          const row = removing;
          if (row === null) return Promise.resolve(null);
          return write(
            (etag) => rankingProcessApi.remove(key, etag, row.participantId, reason),
            row.participantId,
            (edit) =>
              `Removed ${row.displayName} from ${row.place}${where} — ${rowsWord(edit.shifted.length)} moved up.`
          );
        }}
      />
    </section>
  );
}

function InsertForm({
  territorial,
  territory,
  onCancel,
  onInsert
}: {
  territorial: boolean;
  territory: Nullable<string>;
  onCancel: () => void;
  onInsert: (
    pick: Extract<ParticipantPick, { kind: 'person' }>,
    place: number,
    territory: string,
    reason: string
  ) => Promise<Nullable<string>>;
}) {
  const suggestions = React.useContext(TerritorySuggestions);
  const [pick, setPick] = React.useState<Nullable<ParticipantPick>>(null);
  const [place, setPlace] = React.useState('');
  const [target, setTarget] = React.useState(territory ?? '');
  const [reason, setReason] = React.useState('');
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const { key } = useRanking();
  // Whether staff typed in the territory box: what they typed is never replaced (SR-319).
  const typed = React.useRef(false);
  const chosenId = pick?.kind === 'person' ? pick.participantId : null;
  // A participant chosen on a territorial list: their territory in force for this list fills the
  // box, unless staff typed one — one read per participant chosen, a failed one saying nothing. A
  // participant with none puts back the box's first value, never the previous participant's.
  React.useEffect(() => {
    if (!territorial || chosenId === null) return;
    const controller = new AbortController();
    rankingsApi
      .territories(chosenId, key.surveyCode, controller.signal)
      .then((read) => {
        const inForce = read.territories.find((row) => row.list === key.list)?.territory;
        setTarget((current) =>
          typed.current && current.trim() !== '' ? current : (inForce ?? territory ?? '')
        );
      })
      .catch((error: unknown) => {
        if (!(error instanceof ApiError) && !controller.signal.aborted) console.error(error);
      });
    return () => controller.abort();
  }, [territorial, chosenId, key.surveyCode, key.list, territory]);
  const placeNumber = Number(place);
  const ready =
    pick?.kind === 'person' &&
    place.trim() !== '' &&
    Number.isFinite(placeNumber) &&
    (!territorial || target.trim() !== '') &&
    reason.trim() !== '';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready || pick?.kind !== 'person') return;
    setBusy(true);
    setSaid(null);
    const answer = await onInsert(pick, placeNumber, target.trim(), reason.trim());
    setBusy(false);
    setSaid(answer);
  };

  return (
    <form
      aria-label="Insert a participant"
      onSubmit={(event) => void submit(event)}
      className="grid gap-3 rounded-md border border-border bg-white p-4"
    >
      <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_6rem_minmax(0,1fr)]">
        <div className="grid gap-1 text-sm">
          <span className="text-muted-foreground">Participant</span>
          <ParticipantPicker
            label="Participant"
            value={pick}
            onChange={setPick}
            initialQuery=""
            everyParticipant
            placeholder="Search People"
          />
        </div>
        <label className="grid gap-1 text-sm">
          <span className="text-muted-foreground">Place</span>
          <Input
            inputMode="numeric"
            value={place}
            onChange={(event) => setPlace(event.target.value)}
          />
        </label>
        {territorial && (
          <label className="grid gap-1 text-sm">
            <span className="text-muted-foreground">Territory</span>
            <Input
              value={target}
              list={suggestions}
              onChange={(event) => {
                typed.current = true;
                setTarget(event.target.value);
              }}
            />
          </label>
        )}
      </div>
      <label className="grid gap-1 text-sm">
        <span className="text-muted-foreground">Reason</span>
        <Textarea
          rows={2}
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      {said && (
        <p role="alert" className="text-sm text-destructive">
          {said}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!ready || busy}>
          {busy ? 'Inserting…' : 'Insert at this place'}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function MoveDialog({
  row,
  territorial,
  territory,
  onClose,
  onClosed,
  onMove
}: {
  row: Nullable<IRankingTerritoryPlacement>;
  territorial: boolean;
  territory: Nullable<string>;
  onClose: () => void;
  /** Called once the dialog has gone (`ReasonDialog`'s). */
  onClosed: () => void;
  onMove: (
    row: IRankingTerritoryPlacement,
    place: number,
    territory: string,
    reason: string
  ) => Promise<Nullable<string>>;
}) {
  const suggestions = React.useContext(TerritorySuggestions);
  const [place, setPlace] = React.useState('');
  const [target, setTarget] = React.useState('');
  const [opened, setOpened] = React.useState<Nullable<string>>(null);
  if (row && opened !== row.participantId) {
    setOpened(row.participantId);
    setPlace(String(row.place));
    setTarget(territory ?? '');
  }
  if (!row && opened !== null) setOpened(null);
  const placeNumber = Number(place);
  const incomplete =
    place.trim() === '' || !Number.isFinite(placeNumber) || (territorial && target.trim() === '');
  return (
    <ReasonDialog
      open={row !== null}
      onOpenChange={(open) => !open && onClose()}
      onClosed={onClosed}
      title={row ? `Move ${row.displayName}` : 'Move'}
      description={
        row
          ? `From place ${row.place}${territorial && territory ? ` in ${territory}` : ''}. The rows between renumber.`
          : ''
      }
      required
      confirmLabel="Move"
      incomplete={incomplete}
      onConfirm={(reason) =>
        row ? onMove(row, placeNumber, target.trim(), reason) : Promise.resolve(null)
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1 text-sm">
          <span className="text-muted-foreground">New place</span>
          <Input
            inputMode="numeric"
            value={place}
            onChange={(event) => setPlace(event.target.value)}
          />
        </label>
        {territorial && (
          <label className="grid gap-1 text-sm">
            <span className="text-muted-foreground">Territory</span>
            <Input
              value={target}
              list={suggestions}
              onChange={(event) => setTarget(event.target.value)}
            />
          </label>
        )}
      </div>
    </ReasonDialog>
  );
}

/** An edit's status line's tail when the participant's row is flagged: the sentences in full. */
function flaggedNote(edit: IRankingEdit): string {
  const flags = edit.placement?.flags ?? [];
  return flags.length === 0 ? '' : ` Flagged: ${flags.map((flag) => flag.message).join(' ')}`;
}
